'use client';

import { useState } from 'react';
import { noop } from 'lodash';
import styled from 'styled-components';
import { useApolloClient } from '@apollo/client';
import type { ShareSettingsFragment } from '@frameio/graphql-client/generated';
import {
  ShareSettings,
  useGetSharesForAddToShareMenuQuery,
} from '@frameio/graphql-client/generated';
import { SearchMenuItems } from '@frameio/shared/components/SearchMenuItems';
import { useIsMatchingClientName } from '@frameio/shared/contexts/ClientNameContext';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { useCreateShare } from '@frameio/shared/shares/hooks';
import { EMPTY_ARRAY } from '@frameio/utils';
import {
  Box,
  MenuButton,
  MenuGroup,
  MenuNonInteractiveArea,
  MenuPrimaryAction,
  Text,
  Tooltip,
} from '@frameio/vapor';
import { IconPlus, IconReviews, IconShareAdd } from '@frameio/vapor-icons';
import { useAddAssetsToShare } from '../../hooks/useAddAssetsToShare';
import TEXT from './generated/TEXT/AddToShareSubmenu';

TEXT.DEFINE({
  /** a message to display when sharing is disabled */
  defaultDisabledTooltipText: 'Sharing is disabled',
  /** a label for the 'add to share links' menu item */
  labelShare: 'Add to Share Links',
  /** a label for the 'add to existing share links' menu item */
  labelExistingShares: 'Add to Existing Share Links',
  /** a label for the 'existing share links' header */
  labelSharesList: 'Existing Share Links',
  /** a label for the 'shares list description' */
  labelSharesListDescription: (count: number) => `Showing ${count} share links`,
  /** a text for the 'new share link' button */
  newShare: 'New Share Link',
  /** a message to display when there are no results */
  noResults: 'No results',
  /** a placeholder for the search input */
  searchPlaceholder: (count: number) => `Search ${count} Share Links`,
});

const SHARE_COUNT_TO_DISPLAY = 50;
const MINIMUM_COUNT_FOR_SEARCH = 8;

type AddToShareSubmenuProps = {
  assetIds?: Array<string>;
  disabled?: boolean;
  disabledTooltipText?: string;
  onPress?: () => void;
  projectId: string;
  trackingSource?: string;
};

export function AddToShareSubmenu({
  assetIds = EMPTY_ARRAY,
  disabled = false,
  disabledTooltipText = TEXT.defaultDisabledTooltipText,
  onPress = noop,
  projectId,
  trackingSource = 'AssetContextMenu',
}: AddToShareSubmenuProps) {
  const track = useTrackUnsafe();
  const { createShareFromAssets } = useCreateShare();
  const isWebClient = useIsMatchingClientName('web');

  const handleCreateShare = async () => {
    const share = await createShareFromAssets(
      {
        assetIds,
        projectId,
      },
      { shouldRedirect: isWebClient }
    );

    if (!share) {
      return;
    }

    track('static-share-created', {
      num_items_selected: assetIds.length,
      project_id: projectId,
      source: trackingSource,
      is_share_url_copied_automatically: false,
      share_id: share.data?.createShareFromAssets?.share?.id,
    });
    onPress();
  };

  return (
    <Tooltip disabled={!disabled} title={disabledTooltipText}>
      <span>
        <MenuButton
          disabled={disabled}
          iconBefore={<IconShareAdd />}
          label={TEXT.labelShare}
          onPress={handleCreateShare}
          shouldCloseMenuOnSelect={true}
          submenu={
            <ShareSubmenu
              assetIds={assetIds}
              isStandalone={false} // When the SareSubmenu is rendered by itself (not inside a menu button) it's standalone
              onPress={onPress}
              projectId={projectId}
              trackingSource={trackingSource}
            />
          }
        >
          {TEXT.labelShare}
        </MenuButton>
      </span>
    </Tooltip>
  );
}

type ShareSubmenuProps = {
  assetIds?: Array<string>;
  onPress?: () => void;
  projectId: string;
  isStandalone?: boolean;
  trackingSource: string;
};

export const ShareSubmenu = ({
  assetIds = EMPTY_ARRAY,
  onPress = noop,
  projectId,
  isStandalone = true,
  trackingSource,
}: ShareSubmenuProps) => {
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const track = useTrackUnsafe();
  const { createShareFromAssets } = useCreateShare();
  const isWebClient = useIsMatchingClientName('web');
  const { data, previousData } = useGetSharesForAddToShareMenuQuery({
    variables: {
      projectId,
      filters: {
        aggregationMode: 'STATIC',
        origin: 'AD_HOC_ASSETS',
        name: searchQuery,
      },
      page: { first: SHARE_COUNT_TO_DISPLAY },
    },
  });
  const { cache } = useApolloClient();
  const { addAssetsToShare } = useAddAssetsToShare();

  const sharesData = data ?? previousData;
  const shares = sharesData?.project?.shares?.nodes;
  const totalCount = sharesData?.project?.shares?.totalCount ?? 0;
  const unfilteredStaticSharesTotalCount =
    sharesData?.project?.unfilteredStaticShares?.totalCount ?? 0;
  const displayedCount =
    totalCount < SHARE_COUNT_TO_DISPLAY ? totalCount : SHARE_COUNT_TO_DISPLAY;

  const handleSearch = (query: string) => {
    setSearchQuery(query || undefined);
  };
  const handleCreateShare = async () => {
    const share = await createShareFromAssets(
      {
        assetIds,
        projectId,
      },
      { shouldRedirect: isWebClient }
    );

    if (!share) {
      return;
    }

    track('static-share-created', {
      num_items_selected: assetIds.length,
      project_id: projectId,
      source: trackingSource,
      is_share_url_copied_automatically: false,
      share_id: share.data?.createShareFromAssets?.share?.id,
    });
    onPress();
  };

  const handleAddAssetsToShare = async (shareId: string) => {
    const didAddAssets = await addAssetsToShare({
      assetIds,
      projectId,
      shareId,
    });
    if (!didAddAssets) {
      return;
    }

    const shareFragment = cache.readFragment<ShareSettingsFragment>({
      fragment: ShareSettings,
      fragmentName: 'ShareSettings',
      id: cache.identify({ __typename: 'Share', id: shareId }),
      returnPartialData: true,
    });

    track('share-assets-added', {
      creator_user_id: shareFragment?.creator?.id,
      num_items_added: assetIds?.length,
      share_id: shareId,
      source: trackingSource,
    });

    onPress();
  };

  return (
    <StyledSubmenu>
      <MenuGroup>
        <MenuPrimaryAction
          color="primary"
          onSelect={handleCreateShare}
          style={{ display: 'flex' }}
        >
          <IconPlus /> {TEXT.newShare}
        </MenuPrimaryAction>
      </MenuGroup>
      <SearchMenuItems
        isSearchVisible={
          unfilteredStaticSharesTotalCount > MINIMUM_COUNT_FOR_SEARCH ||
          Boolean(searchQuery)
        }
        label={isStandalone ? TEXT.labelExistingShares : TEXT.labelSharesList}
        onRequestSearch={handleSearch}
        placeholder={
          TEXT.searchPlaceholder(unfilteredStaticSharesTotalCount) as string
        }
      >
        {totalCount > SHARE_COUNT_TO_DISPLAY && (
          <Box alignItems="center" display="flex" height={32} paddingLeft={8}>
            <Text color="tertiary" variant="secondary">
              {TEXT.labelSharesListDescription(displayedCount)}
            </Text>
          </Box>
        )}
        {shares && shares.length > 0 ? (
          shares.map((share) => (
            <MenuButton
              key={share.id}
              href={`/share/${share.id}`}
              iconBefore={<IconReviews size={24} />}
              onPress={() => handleAddAssetsToShare(share.id)}
              shouldCloseMenuOnSelect={true}
            >
              <Box paddingLeft={4}>
                <Text truncate={true} variant="body">
                  {share.name}
                </Text>
              </Box>
            </MenuButton>
          ))
        ) : (
          <StyledNoResultsMessage>
            <MenuNonInteractiveArea>{TEXT.noResults}</MenuNonInteractiveArea>
          </StyledNoResultsMessage>
        )}
      </SearchMenuItems>
    </StyledSubmenu>
  );
};

const StyledNoResultsMessage = styled.div`
  margin-left: 4px;
`;

const StyledSubmenu = styled.div`
  width: 240px;
`;
